import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AdminOnlyGuard } from '@core/guards/admin-only.guard';
import { AuthGuard } from '@core/guards/auth.guard';
import { MaliciousUserGuard } from '@core/guards/malicious-user.guard';
import { NotCollegeGuard } from '@core/guards/not-college.guard';
import { PendingChangesGuard } from '@core/guards/pending-changes.guard';
import { EmailTemplatesGuard } from '@core/guards/email-templates.guard';
import { BrokerageType } from '@models/matter';
import { AccountingComponent } from 'src/app/private/sale/accounting/accounting.component';
import { MaliciousUserPageComponent } from './malicious-user-page/malicious-user-page.component';
import { AppMainComponent } from './private/app-main/app-main.component';
import {
	CreateMatterComponent,
	CreationStepStates,
} from './private/create-matter/create.component';
import { CreateMatterSummaryComponent } from './private/create-matter/summary/summary.component';
import { BillingComponent } from './private/manage/billing/billing.component';
import { BusinessChecklistComponent } from './private/manage/business-checklist/business-checklist.component';
import { BusinessDetailsComponent } from './private/manage/business-details/business-details.component';
import { CurrentUsersComponent } from './private/manage/current-users/current-users.component';
import { DefaultsComponent } from './private/manage/defaults/defaults.component';
import { DocumentFormattingComponent } from './private/manage/documents/formatting/formatting.component';
import { DocumentStylingComponent } from './private/manage/documents/styling/document-styling.component';
import { TemplateVersioningComponent } from './private/manage/documents/templates/template-versioning/template-versioning.component';
import { DocumentsTemplatesComponent } from './private/manage/documents/templates/templates.component';
import { BusinessFinancesGeneralComponent } from './private/manage/finances/business-finances-general/business-finances-general.component';
import { InvitesComponent } from './private/manage/invites/invites.component';
import { RequisitionsTemplatesComponent } from './private/manage/requisitions-templates/requisitions-templates.component';
import { SecurityComponent } from './private/manage/security/security.component';
import { StatementOfAccountGeneralComponent } from './private/manage/statement-of-account/general/general.component';
import { TrustAccountsComponent } from './private/manage/trust-accounts/trust-accounts.component';
import { ProfileDetailsComponent } from './private/profile/details/details.component';
import { ChicagoProfileComponent } from './private/profile/integrations/chicago/chicago.component';
import { CloserSyncComponent } from './private/profile/integrations/closer-sync/closer-sync.component';
import { FctIntegrationComponent } from './private/profile/integrations/fct/fct.component';
import { IntegrationsComponent } from './private/profile/integrations/integrations.component';
import { LlcIntegrationComponent } from './private/profile/integrations/llc/llc.component';
import { StewartProfileComponent } from './private/profile/integrations/stewart/stewart.component';
import { ProfileTeranetComponent } from './private/profile/integrations/teranet/teranet.component';
import { TitlePlusProfileComponent } from './private/profile/integrations/title-plus/title-plus-profile.component';
import { TreefortProfileComponent } from './private/profile/integrations/treefort/treefort.component';
import { ProfilePreferencesComponent } from './private/profile/preferences/preferences.component';
import { ProfileComponent } from './private/profile/profile.component';
import { AccountsComponent } from './private/sale/accounts/accounts.component';
import { AdjustmentsComponent } from './private/sale/adjustments/adjustments.component';
import { DepositSideFormComponent } from './private/sale/adjustments/side-form/deposit/deposit-side-form.component';
import { ApsSideFormComponent } from './private/sale/aps/aps-side-form/aps-side-form.component';
import { ApsComponent } from './private/sale/aps/aps.component';
import { UndertakingsComponent } from './private/sale/closing/undertakings';
import { CommissionBrokerageSelectionComponent } from './private/sale/commission/commission-brokerage-selection/commission-brokerage-selection.component';
import { CommissionComponent } from './private/sale/commission/commission.component';
import { DebtsComponent } from './private/sale/debts';
import { DocumentComponent } from './private/sale/documents/document/document.component';
import { FormViewerComponent } from './private/sale/documents/forms/form-viewer/form-viewer.component';
import { FormsComponent } from './private/sale/documents/forms/forms.component';
import { PurchaseRequisitionsComponent } from './private/sale/documents/requisitions/requisitions.component';
import { ResponseToRequisitionsComponent } from './private/sale/documents/response-to-requisitions/response-to-requisitions.component';
import { UploadedDocumentsComponent } from './private/sale/documents/uploaded/uploaded.component';
import { EncumbranceSideFormComponent } from './private/sale/encumbrances/encumbrance/components/side-form/encumbrance-side-form.component';
import { PaymentCalculatorSideFormComponent } from './private/sale/encumbrances/encumbrance/components/side-form/payment-calculator/payment-calculator-side-form.component';
import { EncumbranceComponent } from './private/sale/encumbrances/encumbrance/encumbrance.component';
import { EncumbrancesComponent } from './private/sale/encumbrances/encumbrances.component';
import { ExecutionAffidavitsComponent } from './private/sale/execution-affidavits/execution-affidavits.component';
import { EditBrokerSideFormComponent } from './private/sale/fields/brokerage/broker/side-form/broker-edit/broker-edit-side-form.component';
import { EditBrokerageSideFormComponent } from './private/sale/fields/brokerage/side-form/edit/edit-brokerage-side-form.component';
import { EditClientSideFormComponent } from './private/sale/fields/client/simple-edit/side-form/edit-client/edit-client-side-form.component';
import { EditLawFirmSideFormComponent } from './private/sale/fields/law-firms/side-form/edit/law-firms-edit-side-form.component';
import { EditLawyerSideFormComponent } from './private/sale/fields/law-firms/side-form/lawyer/edit/lawyer-edit-side-form.component';
import { SaleHomeComponent } from './private/sale/home/home.component';
import { LedgerComponent } from './private/sale/ledger/ledger.component';
import { LttComponent } from './private/sale/ltt/ltt.component';
import { CloserSyncCreateMortgageComponent } from './private/sale/mortgages/closer-sync/closer-sync-create-mortgage/closer-sync-create-mortgage.component';
import { ResourcesComponent } from './private/sale/mortgages/llc/components/resources/resources.component';
import { LlcDataComponent } from './private/sale/mortgages/llc/llc-data/llc-data.component';
import { LlcResourcesComponent } from './private/sale/mortgages/llc/llc-resources/llc-resources.component';
import { MatterMortgageComponent } from './private/sale/mortgages/mortgage/mortgage.component';
import { MatterMortgagesComponent } from './private/sale/mortgages/mortgages.component';
import { MortgageIntakeComponent } from './private/sale/mortgages/stepper/mortgage-intake.component';
import { PartiesClientsComponent } from './private/sale/parties/clients/clients.component';
import { PartiesMortgagorsComponent } from './private/sale/parties/mortgagors/mortgagors.component';
import { PartiesOtherPartiesComponent } from './private/sale/parties/other-parties/other-parties.component';
import { PartiesComponent } from './private/sale/parties/parties.component';
import { ComplianceLettersComponent } from './private/sale/property/compliance-letters/compliance-letters.component';
import { PropertyCondoCorpComponent } from './private/sale/property/condo-corp/condo-corp.component';
import { PropertyDetailsComponent } from './private/sale/property/details/details.component';
import { FireInsuranceComponent } from './private/sale/property/fire-insurance/fire-insurance.component';
import { EditFireInsuranceBrokerageComponent } from './private/sale/property/fire-insurance/side-form/edit/fire-insurance-brokerage/edit-fire-insurance-brokerage.component';
import { EditFireInsuranceCompanyComponent } from './private/sale/property/fire-insurance/side-form/edit/fire-insurance-company/edit-fire-insurance-company.component';
import { FireInsuranceSideFormComponent } from './private/sale/property/fire-insurance/side-form/fire-insurance-side-form.component';
import { PropertyComponent } from './private/sale/property/property.component';
import { SaleComponent } from './private/sale/sale.component';
import { TeranetDefaultComponent } from './private/sale/teranet/default/default.component';
import { AdjacentPropertiesMapComponent } from './private/sale/teranet/map/map.component';
import { TeranetOverviewComponent } from './private/sale/teranet/overview/overview.component';
import { TeranetStepperAdjacentAnalysisComponent } from './private/sale/teranet/stepper/adjacent-analysis/adjacent-analysis.component';
import { TeranetStepperInstrumentsComponent } from './private/sale/teranet/stepper/instruments/instruments.component';
import { TeranetStepperRequisitionComponent } from './private/sale/teranet/stepper/requisition/requisition.component';
import { TeranetStepperSearchComponent } from './private/sale/teranet/stepper/search/search.component';
import { StepperComponent } from './private/sale/teranet/stepper/stepper.component';
import { TeranetStepperSyncComponent } from './private/sale/teranet/stepper/sync/sync.component';
import { TeranetStepperWritsComponent } from './private/sale/teranet/stepper/writs/writs.component';
import { TeranetComponent } from './private/sale/teranet/teranet.component';
import { WritComponent } from './private/sale/teranet/writ/writ.component';
import { TitleInsuranceComponent } from './private/sale/title-insurance/title-insurance.component';
import { StewartCallbackComponent } from './private/stewart-callback/stewart-callback.component';
import { OutlookCallbackComponent } from './private/email-integration/outlook-callback/outlook-callback.component';
import { TitlePlusCallbackComponent } from './private/title-plus-callback/title-plus-callback.component';
import { AuthComponent } from './public/auth/auth.component';
import { ForgotComponent } from './public/auth/login/forgot/forgot.component';
import { ResetComponent } from './public/auth/login/forgot/reset/reset.component';
import { LoginComponent } from './public/auth/login/login.component';
import { PendingInviteComponent } from './public/auth/pending-invite/pending-invite.component';
import { CreateUserComponent } from './public/auth/register/create-user/create-user.component';
import { RegisterComponent } from './public/auth/register/register.component';
import { VerifyEmailComponent } from './public/auth/verify-email/verify-email.component';
import { ContactComponent } from './public/contact/contact.component';
import { FourohfourComponent } from './public/invalid/fourohfour/fourohfour.component';
import { FourohthreeComponent } from './public/invalid/fourohthree/fourohthree.component';
import { LandingComponent } from './public/landing/landing.component';
import { LegalComponent } from './public/legal/legal.component';
import { ProxyTestComponent } from './public/proxy-test/proxy-test.component';
import { EncumbranceExists } from '@core/guards/encumbrance-exists.guard';
import { LoadedFromLeapGuard } from '@core/guards/loaded-from-leap.guard';
import { MatterRestrictedGuard } from '@core/guards/matter-restricted-user.guard';
import { RefreshGuard } from '@core/guards/refresh.guard';
import { sc16163EnableSignItGuard } from '@core/guards/sc16163EnableSignIt.guard';
import { DocxTemplatesComponent } from './private/manage/documents/docx-templates/docx-templates.component';
import { ClientMergeReviewComponent } from './private/profile/integrations/leap/client-merge-review/client-merge-review.component';
import { LeapComponent } from './private/profile/integrations/leap/leap.component';
import { SignItComponent } from './private/profile/integrations/sign-it/sign-it.component';
import { ChargeComponent } from './private/sale/closing/charge/charge.component';
import { ChargesComponent } from './private/sale/closing/charges/charges.component';
import { TransferComponent } from './private/sale/closing/transfer/transfer.component';
import { DocumentsV2Component } from './private/sale/documents-v2/documents-v2.component';
import { MatterPackageComponent } from './private/sale/documents-v2/matter-packages/matter-package/matter-package.component';
import { SaleDocsComponent } from './private/sale/documents/sale-docs/sale-docs.component';
import { SignUpComponent } from './public/auth/sign-up/sign-up.component';
import { PAGE, SITE_SECTION } from './shared/state/navigation/page.model';
import { WordFlowRedirectGuard } from '@core/guards/word-flow-redirect.guard';
import { RequestsComponent } from './private/manage/documents/requests/requests.component';
import { EditFireInsuranceBrokerComponent } from './private/sale/property/fire-insurance/side-form/edit/fire-insurance-broker/edit-fire-insurance-broker.component';
import { EditFireInsuranceAgentComponent } from './private/sale/property/fire-insurance/side-form/edit/fire-insurance-agent/edit-fire-insurance-agent.component';
import { DocumentRequestsGuard } from '@core/guards/document-requests.guard';
import { GenericFormsComponent } from './private/sale/documents/generic-forms/generic-forms.component';
import { sc20628EnableGenericFormsGuard } from '@core/guards/sc20628EnableGenericForms.guard';
import { GenericFormPdfViewerComponent } from './private/sale/documents/generic-forms/generic-form-pdf-viewer/generic-form-pdf-viewer.component';
import { ClientIntakePageComponent } from './private/sale/parties/client-intake-page/client-intake-page.component';
import { ClientIntakeReviewResponsesPageComponent } from './private/sale/parties/client-intake-review-responses-page/client-intake-review-responses-page.component';
import { EmailTemplatesComponent } from './private/manage/client-engagement/email-templates/email-templates.component';
import { EmailTemplateComponent } from './private/manage/client-engagement/email-templates/email-template/email-template.component';
import { EnvelopeComponent } from './private/sale/documents/envelopes/envelope/envelope.component';

const routes: Routes = [
	{
		path: '',
		component: AuthComponent,
		children: [
			{
				path: '',
				redirectTo: 'login',
				pathMatch: 'full',
			},
			{
				path: '404',
				component: FourohfourComponent,
			},
			{
				path: '403',
				component: FourohthreeComponent,
			},
			{
				path: 'login',
				component: LoginComponent,
			},
			{
				path: 'sign-up',
				component: SignUpComponent,
			},
			{
				path: 'forgot',
				component: ForgotComponent,
			},
			{
				path: 'reset',
				component: ResetComponent,
			},
			{
				path: 'invitation/accept/:inviteId',
				component: RegisterComponent,
			},
			{
				path: 'verify-email',
				component: VerifyEmailComponent,
				pathMatch: 'full',
				canActivate: [AuthGuard],
			},
			{
				path: 'pending-invite',
				component: PendingInviteComponent,
				pathMatch: 'full',
				canActivate: [AuthGuard],
			},
			{
				path: 'register',
				children: [
					{
						path: '',
						pathMatch: 'full',
						component: RegisterComponent,
					},
					{
						path: 'user',
						component: CreateUserComponent,
						canActivate: [AuthGuard],
					},
				],
			},
		],
	},
	{
		path: 'leap',
		loadChildren: () =>
			import('./public/leap/leap.module').then((m) => m.LeapModule),
	},
	{
		path: 'contact',
		component: ContactComponent,
	},
	{
		path: 'legal',
		component: LegalComponent,
	},
	{
		path: 'landing',
		component: LandingComponent,
	},
	{
		path: 'library',
		loadChildren: () =>
			import('./shared/atomic/atomic.module').then((m) => m.AtomicModule),
	},
	{
		path: 'proxy',
		component: ProxyTestComponent,
	},
	{
		path: 'stewart-auth-callback',
		canActivate: [AuthGuard, MaliciousUserGuard],
		component: AppMainComponent,
		runGuardsAndResolvers: 'always',
		children: [
			{
				path: '',
				component: StewartCallbackComponent,
			},
		],
	},
	{
		path: 'outlook-auth-callback',
		// canActivate: [AuthGuard, MaliciousUserGuard],
		component: AppMainComponent,
		// runGuardsAndResolvers: 'always',
		children: [
			{
				path: '',
				component: OutlookCallbackComponent,
			},
		],
	},
	{
		path: 'app',
		canMatch: [AuthGuard, RefreshGuard, MaliciousUserGuard],
		component: AppMainComponent,
		runGuardsAndResolvers: 'always',
		children: [
			{
				path: '',
				loadChildren: () =>
					import('./private/home/home.module').then((m) => m.HomeModule),
			},
			{
				path: 'auth/titleplus',
				component: TitlePlusCallbackComponent,
			},
			{
				path: 'f5596e17-338b-4898-8a71-a05b04adf13b',
				canActivate: [NotCollegeGuard],
				loadChildren: () =>
					import('./global-data-management/global-data-management.module').then(
						(m) => m.GlodalDataManagementModule,
					),
			},
			{
				path: 'library',
				loadChildren: () =>
					import('./shared/atomic/atomic.module').then((m) => m.AtomicModule),
			},
			{
				path: 'llc-resources',
				component: ResourcesComponent,
			},
			{
				path: 'create/:matterId',
				component: CreateMatterComponent,
				data: { section: SITE_SECTION.MatterCreation },
				children: [
					{
						path: 'aps',
						component: ApsComponent,
						data: { step: CreationStepStates.APS },
						children: [
							{
								path: '',
								component: ApsSideFormComponent,
								data: { title: 'APS intake' },
							},
							{
								path: 'edit-client',
								component: EditClientSideFormComponent,
								data: { title: 'Edit client' },
							},
							{
								path: 'edit-brokerage',
								component: EditBrokerageSideFormComponent,
							},
							{
								path: 'edit-agent',
								component: EditBrokerSideFormComponent,
							},
							{
								path: 'edit-law-firm',
								component: EditLawFirmSideFormComponent,
							},
							{
								path: 'edit-lawyer',
								component: EditLawyerSideFormComponent,
							},
							{
								path: 'edit-deposits',
								component: DepositSideFormComponent,
							},
						],
					},
					{
						path: 'commission',
						component: CommissionComponent,
						data: { step: CreationStepStates.COMMISSION },
						children: [
							{
								path: 'seller-brokerage',
								component: CommissionBrokerageSelectionComponent,
								data: {
									page: BrokerageType.SELLER_BROKERAGE,
								},
							},
							{
								path: 'buyer-brokerage',
								component: CommissionBrokerageSelectionComponent,
								data: {
									page: BrokerageType.BUYER_BROKERAGE,
								},
							},
							{
								path: '',
								redirectTo: 'seller-brokerage',
								pathMatch: 'full',
							},
						],
					},
					{
						path: 'summary',
						component: CreateMatterSummaryComponent,
						data: { step: CreationStepStates.SUMMARY },
					},
					{
						path: '',
						redirectTo: 'aps',
						pathMatch: 'full',
					},
				],
			},
			{
				path: 'profile',
				component: ProfileComponent,
				data: { section: SITE_SECTION.Settings },
				children: [
					{
						path: 'preferences',
						component: ProfilePreferencesComponent,
					},
					{
						path: 'integrations',
						children: [
							{
								path: 'LLC',
								component: LlcIntegrationComponent,
							},
							{
								path: 'FCT',
								component: FctIntegrationComponent,
							},
							{
								path: 'teranet',
								component: ProfileTeranetComponent,
							},
							{
								path: 'stewart',
								component: StewartProfileComponent,
							},
							{
								path: 'chicago',
								component: ChicagoProfileComponent,
							},
							{
								path: 'title-plus',
								component: TitlePlusProfileComponent,
							},
							{
								path: 'treefort',
								component: TreefortProfileComponent,
							},
							{
								path: 'closer-sync',
								component: CloserSyncComponent,
							},
							{
								path: 'leap',
								children: [
									{ path: '', component: LeapComponent },
									{
										path: 'clients-merge-review/:subsection',
										pathMatch: 'full',
										component: ClientMergeReviewComponent,
									},
								],
								canActivate: [LoadedFromLeapGuard],
							},
							{
								path: 'sign-it',
								component: SignItComponent,
								canMatch: [sc16163EnableSignItGuard],
							},
							{
								path: '',
								component: IntegrationsComponent,
							},
						],
					},
					{
						path: 'details',
						component: ProfileDetailsComponent,
					},
					{
						path: 'trust-accounts',
						pathMatch: 'full',
						component: TrustAccountsComponent,
						canActivate: [AdminOnlyGuard],
					},
					{
						path: 'defaults',
						pathMatch: 'full',
						component: DefaultsComponent,
						canActivate: [AdminOnlyGuard],
					},
					{
						path: 'invites',
						pathMatch: 'full',
						component: InvitesComponent,
						canActivate: [AdminOnlyGuard],
					},
					{
						path: 'billing',
						pathMatch: 'full',
						component: BillingComponent,
						canActivate: [NotCollegeGuard, AdminOnlyGuard],
					},
					{
						path: 'business-details',
						pathMatch: 'full',
						canActivate: [AdminOnlyGuard],
						component: BusinessDetailsComponent,
					},
					{
						path: 'security',
						pathMatch: 'full',
						canActivate: [AdminOnlyGuard],
						component: SecurityComponent,
					},
					{
						path: 'templates',
						canActivate: [AdminOnlyGuard],
						children: [
							{
								path: 'documents',
								children: [
									{
										path: '',
										component: DocumentsTemplatesComponent,
									},
									{
										path: ':templateId',
										children: [
											{
												path: '',
												component: TemplateVersioningComponent,
												canDeactivate: [PendingChangesGuard],
											},
										],
									},
								],
							},
							{
								path: 'docx-templates',
								children: [
									{
										path: '',
										component: DocxTemplatesComponent,
									},
								],
							},
							{
								path: 'requisitions',
								component: RequisitionsTemplatesComponent,
							},
						],
					},
					{
						path: 'users',
						pathMatch: 'full',
						canActivate: [AdminOnlyGuard],
						component: CurrentUsersComponent,
					},
					{
						path: 'statement-of-account/general',
						pathMatch: 'full',
						canActivate: [AdminOnlyGuard],
						component: StatementOfAccountGeneralComponent,
					},
					{
						path: 'finances/general',
						pathMatch: 'full',
						canActivate: [AdminOnlyGuard],
						component: BusinessFinancesGeneralComponent,
					},
					{
						path: 'checklists',
						pathMatch: 'full',
						canActivate: [AdminOnlyGuard],
						component: BusinessChecklistComponent,
					},
					{
						path: 'document/formatting',
						pathMatch: 'full',
						canActivate: [AdminOnlyGuard],
						component: DocumentFormattingComponent,
					},
					{
						path: 'requests',
						pathMatch: 'full',
						canActivate: [DocumentRequestsGuard],
						component: RequestsComponent,
					},
					{
						path: 'document/styling',
						pathMatch: 'full',
						canActivate: [AdminOnlyGuard],
						component: DocumentStylingComponent,
					},
					{
						path: '',
						pathMatch: 'full',
						component: ProfileDetailsComponent,
					},
					{
						path: 'email-templates',
						canActivate: [AdminOnlyGuard, EmailTemplatesGuard],
						children: [
							{
								path: '',
								component: EmailTemplatesComponent,
							},
							{
								path: ':templateId',
								component: EmailTemplateComponent,
							},
						],
					},
				],
			},
			{
				path: 'matter/:matterId',
				component: SaleComponent,
				data: { section: SITE_SECTION.Matter },
				canMatch: [MatterRestrictedGuard],
				children: [
					{
						path: 'encumbrances',
						component: EncumbrancesComponent,
						children: [
							{
								path: ':encumbranceId',
								component: EncumbranceComponent,
								canActivate: [EncumbranceExists],
								children: [
									{
										path: '',
										component: EncumbranceSideFormComponent,
									},
									{
										path: 'payment-calculator',
										component: PaymentCalculatorSideFormComponent,
									},
								],
							},
						],
					},
					{
						path: 'closerSYNC/create/:transactionId',
						component: CloserSyncCreateMortgageComponent,
					},
					{
						path: 'mortgages',
						component: MatterMortgagesComponent,
						children: [
							{
								path: ':mortgageId',
								component: MatterMortgageComponent,
							},
						],
					},
					{
						path: 'mortgages/:mortgageId/intake',
						component: MortgageIntakeComponent,
					},
					{
						path: 'mortgages/:mortgageId/data',
						component: LlcDataComponent,
					},
					{
						path: 'mortgages/:mortgageId/resources',
						component: LlcResourcesComponent,
					},
					{
						path: 'commission',
						component: CommissionComponent,
						children: [
							{
								path: 'seller-brokerage',
								component: CommissionBrokerageSelectionComponent,
								data: {
									page: BrokerageType.SELLER_BROKERAGE,
									showHeader: true,
								},
							},
							{
								path: 'buyer-brokerage',
								component: CommissionBrokerageSelectionComponent,
								data: { page: BrokerageType.BUYER_BROKERAGE, showHeader: true },
							},
							{
								path: '',
								redirectTo: 'seller-brokerage',
								pathMatch: 'full',
							},
						],
					},
					{
						path: 'title-insurance',
						component: TitleInsuranceComponent,
					},
					{
						path: 'title-search',
						component: TeranetComponent,
						children: [
							{
								path: '',
								pathMatch: 'full',
								redirectTo: 'overview',
							},
							{
								path: 'overview',
								component: TeranetOverviewComponent,
								children: [
									{
										path: 'map',
										component: AdjacentPropertiesMapComponent,
									},
									{
										path: 'writs',
										component: WritComponent,
									},
									{
										path: 'search-overview',
										component: TeranetDefaultComponent,
									},
									{
										path: '',
										redirectTo: 'search-overview',
										pathMatch: 'full',
									},
								],
							},
							{
								path: 'stepper',
								component: StepperComponent,
								children: [
									{
										path: 'search',
										component: TeranetStepperSearchComponent,
									},
									{
										path: 'sync',
										component: TeranetStepperSyncComponent,
									},
									{
										path: 'instruments',
										component: TeranetStepperInstrumentsComponent,
									},
									{
										path: 'adjacent-analysis',
										component: TeranetStepperAdjacentAnalysisComponent,
									},
									{
										path: 'writs',
										component: TeranetStepperWritsComponent,
									},
									{
										path: 'requisitions',
										component: TeranetStepperRequisitionComponent,
									},
									{
										path: '',
										component: TeranetStepperSearchComponent,
									},
								],
							},
						],
					},
					{
						path: 'review-aps',
						component: ApsComponent,
						children: [
							{
								path: '',
								component: ApsSideFormComponent,
							},
							{
								path: 'edit-client',
								component: EditClientSideFormComponent,
							},
							{
								path: 'edit-brokerage',
								component: EditBrokerageSideFormComponent,
							},
							{
								path: 'edit-agent',
								component: EditBrokerSideFormComponent,
							},
							{
								path: 'edit-law-firm',
								component: EditLawFirmSideFormComponent,
							},
							{
								path: 'edit-lawyer',
								component: EditLawyerSideFormComponent,
							},
							{
								path: 'edit-deposits',
								component: DepositSideFormComponent,
							},
						],
					},
					{
						path: 'closing',
						children: [
							{
								path: 'charges',
								component: ChargesComponent,
								children: [
									{
										path: ':mortgageId',
										component: ChargeComponent,
									},
								],
							},
							{
								path: 'transfer',
								component: TransferComponent,
							},
							{
								path: 'accounting',
								component: AccountingComponent,
							},
						],
					},
					{
						path: 'execution-affidavits',
						component: ExecutionAffidavitsComponent,
					},
					{
						path: 'ltt',
						component: LttComponent,
					},
					{
						path: 'documents',
						children: [
							{
								path: 'requisitions',
								component: PurchaseRequisitionsComponent,
							},
							{
								path: 'requisitions-response',
								component: ResponseToRequisitionsComponent,
							},
							{
								path: 'docx',
								component: DocumentsV2Component,
								canActivate: [WordFlowRedirectGuard],
							},
							{
								path: 'packages',
								component: DocumentsV2Component,
							},
							{
								path: 'packages/:packageId',
								component: MatterPackageComponent,
								data: {
									section: SITE_SECTION.MatterPackage,
								},
							},
							{
								path: 'envelopes/:packageId',
								component: EnvelopeComponent,
								canMatch: [sc16163EnableSignItGuard],
							},
							{
								path: 'uploaded',
								component: UploadedDocumentsComponent,
							},
							{
								path: 'generic-forms',
								component: GenericFormsComponent,
								canActivate: [sc20628EnableGenericFormsGuard],
								children: [
									{
										path: ':formName',
										component: GenericFormPdfViewerComponent,
									},
								],
							},
							{
								path: 'forms',
								component: FormsComponent,
								canActivate: [sc20628EnableGenericFormsGuard],
								children: [
									{
										path: ':formName',
										component: FormViewerComponent,
									},
									{
										path: ':formName/:mortgageId',
										component: FormViewerComponent,
									},
									{
										path: '',
										component: FormViewerComponent,
									},
								],
							},

							{
								path: 'undertakings',
								component: UndertakingsComponent,
							},
							{
								path: 'overview',
								component: SaleDocsComponent,
							},
							{
								path: '',
								pathMatch: 'full',
								redirectTo: 'overview',
							},
							{
								path: ':documentId',
								component: DocumentComponent,
							},
						],
					},
					{
						path: 'adjustments',
						component: AdjustmentsComponent,
					},
					{
						path: 'accounts',
						component: AccountsComponent,
					},
					{
						path: 'debts',
						component: DebtsComponent,
					},
					{
						path: 'ledger',
						component: LedgerComponent,
					},
					{
						path: 'property',
						component: PropertyComponent,
						children: [
							{
								path: 'details',
								component: PropertyDetailsComponent,
							},
							{
								path: 'condo-corporation',
								component: PropertyCondoCorpComponent,
							},
							{
								path: 'compliance-letters',
								component: ComplianceLettersComponent,
							},
							{
								redirectTo: 'details',
								pathMatch: 'full',
								path: '',
							},
						],
					},
					{
						path: 'fire-insurance',
						component: FireInsuranceComponent,
						children: [
							{
								path: '',
								component: FireInsuranceSideFormComponent,
							},
							{
								path: 'edit-fire-insurance-company',
								component: EditFireInsuranceCompanyComponent,
							},
							{
								path: 'edit-fire-insurance-agent',
								component: EditFireInsuranceAgentComponent,
							},
							{
								path: 'edit-fire-insurance-brokerage',
								component: EditFireInsuranceBrokerageComponent,
							},
							{
								path: 'edit-fire-insurance-broker',
								component: EditFireInsuranceBrokerComponent,
							},
						],
					},
					{
						path: 'jurisdictions',
						loadChildren: () =>
							import(
								'./shared/modules/jurisdictions/jurisdictions.module'
							).then((m) => m.JurisdictionsModule),
					},
					{
						path: 'client-intake',
						component: ClientIntakePageComponent,
					},
					{
						path: 'client-intake-review',
						component: ClientIntakeReviewResponsesPageComponent,
					},
					{
						path: 'parties',
						component: PartiesComponent,
						children: [
							{
								path: 'mortgagors',
								component: PartiesMortgagorsComponent,
								data: { client: true, page: PAGE.MATTER_PARTIES_CLIENTS },
							},
							{
								path: 'clients',
								component: PartiesClientsComponent,
								data: { client: true, page: PAGE.MATTER_PARTIES_CLIENTS },
							},
							{
								path: 'other-parties',
								component: PartiesOtherPartiesComponent,
								data: { client: false, page: PAGE.MATTER_PARTIES_OTHER },
							},
							{
								redirectTo: 'clients',
								pathMatch: 'full',
								path: '',
							},
						],
					},
					{
						path: '',
						pathMatch: 'full',
						redirectTo: 'overview',
					},
					{
						path: 'overview',
						pathMatch: 'full',
						component: SaleHomeComponent,
					},
				],
			},
		],
	},

	// Route for malicious users
	{
		path: 'contact-needed',
		component: MaliciousUserPageComponent,
	},
];

@NgModule({
	imports: [
		RouterModule.forRoot(routes, {
			anchorScrolling: 'enabled',
			paramsInheritanceStrategy: 'always',
			onSameUrlNavigation: 'reload', // This is to ensure that the page is reloaded when the same URL is navigated to and to re-run the guards (useful for authentication for example)
		}),
	],
	exports: [RouterModule],
})
export class AppRoutingModule {}
